<template>
    <div style="position: relative">
        <div class="top-menu" @mouseleave="hideSecond">
            <div
                v-for="(i, e) in menuList"
                :key="e"
                class="top-menu-first"
                :class="{ 'top-menu-item-selected': selectShowFirstIndex === e }"
                @click.stop="handleFirstMenuClick(i, e)"
                @mouseover="hideThird()"
            >
                <NEllipsis
                    v-if="!i.hidden"
                    mx-8
                    class="top-menu-ellipsis"
                    :class="{ 'top-menu-item-selected': selectShowFirstIndex === e }"
                    style="
                        width: 100%;
                        padding-bottom: 3px;
                        padding-top: 2px;
                        font-size: 16px;
                        margin: 0;
                        text-align: center;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    "
                >
                    <!-- 菜单图标 -->
                    <n-icon
                        v-if="i.meta.icon"
                        class="top-menu-ellipsis-icon"
                        :class="{ 'top-menu-ellipsis-icon-selected': selectShowFirstIndex === e }"
                        size="18"
                        style="margin-right: 5px; display: inline-flex; align-items: center; vertical-align: middle; position: relative; top: -2px;"
                        :component="getIcon(i.meta.icon)"
                        :depth="2"
                    />
                    {{ i.meta.title }}
                    <!-- 添加下拉箭头 - 只在有多个二级菜单时显示 -->
                    <n-icon
                        v-if="i.children && i.children.length > 1"
                        class="dropdown-arrow"
                        size="14"
                        style="margin-left: 4px;"
                    >
                        <i-carbon-chevron-down />
                    </n-icon>
                </NEllipsis>
            </div>
        </div>
        <div v-show="showSecondMenu" class="menu-second">
            <div v-for="(i, e) in menuList" v-show="activeMenuIndex === e" :key="e" class="top-menu-second">
                <div
                    v-for="(a, c) in i.children"
                    :key="c"
                    class="top-menu-second-card"
                    :class="{ 'with-third-menu-open': showThirdMenu && activeSecondMenuIndex === a.key }"
                    @click.stop="handleSecondMenuClick(a, c, e)"
                >
                    <div>
                        <NPerformantEllipsis
                            class="top-menu-ellipsis custom-tooltip"
                            style="width: 140px; font-size: 16px"
                            tooltip-class="custom-tooltip"
                        >
                            <span
                                class="secondText"
                                :class="{
                                    'top-menu-item-selected2': selectShowSecondIndex === a.key,
                                }"
                            >
                                {{ a.meta.title }}
                                <!-- 为二级菜单添加下拉箭头 -->
                                <n-icon
                                    v-if="a.children && a.children.length > 0"
                                    class="dropdown-arrow"
                                    size="14"
                                    style="margin-left: 4px; vertical-align: middle;"
                                >
                                    <i-carbon-chevron-down />
                                </n-icon>
                            </span>
                        </NPerformantEllipsis>
                    </div>
                    <!-- 三级菜单，修改为下方显示 -->
                    <div 
                        v-if="a.children && a.children.length > 0 && showThirdMenu && activeSecondMenuIndex === a.key" 
                        class="thirdMenu"
                    >
                        <div
                            v-for="(b, d) in a.children"
                            :key="d"
                            :class="{ 'top-menu-item-selected2': selectShowThirdIndex === b.name }"
                            @click.stop="selectmenuThird(a, b, e)"
                        >
                            {{ b.meta.title }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { NPerformantEllipsis } from 'naive-ui';
import type { PropType } from 'vue';
import { ref, watch } from 'vue';
import { getIcon } from './default-modules/icon';
import { useCommonStore } from '~/stores/common';

const props = defineProps({
    options: {
        type: Array as any,
    },
    defaultValue: {
        type: String,
    },
    onClick: {
        type: Function as PropType<(e?: MouseEvent) => Promise<unknown>>,
    },
});

const emits = defineEmits<{
    (e: 'update:value', key: string, item: any, value?: any): any;
}>();

const loading = ref(false);
const showSpin = ref<boolean>(false);
const selectShowThirdIndex = ref<string | null>(null);
const selectShowSecondIndex = ref<string | null>(null);
const selectShowFirstIndex = ref<number | null>(null);
const showThirdIndex = ref<number | null>(null);
const menuList: any = ref([]);
const activeMenuIndex = ref<number | null>(null);
const activeSecondMenuIndex = ref<string | null>(null); // 活动的二级菜单索引
const currentHoverItem = ref<string | null>(null);

watch(
    () => props.options,
    (v) => {
        menuList.value = v;
        if (props.defaultValue) {
            menuList.value.forEach((a, b) => {
                if (a.key === props.defaultValue) {
                    selectShowFirstIndex.value = b;
                }
                if (a.children) {
                    a.children.forEach((c, d) => {
                        if (c.key === props.defaultValue) {
                            selectShowFirstIndex.value = b;
                            selectShowSecondIndex.value = c.key;
                        }
                        if (c.children) {
                            c.children.forEach((d, e) => {
                                if (d.name === props.defaultValue) {
                                    selectShowFirstIndex.value = b;
                                    selectShowSecondIndex.value = c.key;
                                    selectShowThirdIndex.value = d.name;
                                }
                            });
                        }
                    });
                }
            });
        }
    },
    { immediate: true },
);
watch(
    () => props.defaultValue,
    (v) => {
        if (props.defaultValue) {
            menuList.value.forEach((a, b) => {
                if (a.key === props.defaultValue) {
                    selectShowFirstIndex.value = b;
                }
                if (a.children) {
                    a.children.forEach((c, d) => {
                        if (c.key === props.defaultValue) {
                            selectShowFirstIndex.value = b;
                            selectShowSecondIndex.value = c.key;
                        }
                    });
                }
            });
        }
    },
);

const showSecondMenu = ref<Boolean>(false);
const showThirdMenu = ref<Boolean>(false);
const commonStore = useCommonStore();

const handleFirstMenuClick = (i: any, e: number) => {
    // 特殊处理：只有一个子菜单的情况
    if (i.children && i.children.length === 1) {
        // 获取唯一的子菜单项
        const onlyChild = i.children[0];
        
        // 更新选中状态
        selectShowFirstIndex.value = e;
        selectShowSecondIndex.value = onlyChild.key;
        selectShowThirdIndex.value = null;
        
        // 使用该子菜单的数据进行跳转
        emits('update:value', onlyChild.key, onlyChild, null);
        
        // 隐藏所有菜单
        showSecondMenu.value = false;
        showThirdMenu.value = false;
        activeSecondMenuIndex.value = null;
        
        return; // 退出函数，不执行后面的逻辑
    }
    
    if (!i.children || i.children.length === 0) {
        // 没有子菜单，直接跳转
        selectmenuFirst(i, e);
        
        // 点击首页时隐藏所有二级菜单和三级菜单
        showSecondMenu.value = false;
        showThirdMenu.value = false;
        activeSecondMenuIndex.value = null;
    } else {
        // 更新选中的一级菜单索引，显示白色背景
        selectShowFirstIndex.value = e;
        
        // 有子菜单，切换二级菜单的显示/隐藏
        if (activeMenuIndex.value === e && showSecondMenu.value) {
            // 如果点击的是当前展开的菜单，则隐藏二级菜单
            showSecondMenu.value = false;
        } else {
            // 显示此菜单的二级菜单并更新活动菜单索引
            activeMenuIndex.value = e;
            showSecondMenu.value = true;
        }
        // 点击一级菜单时隐藏所有三级菜单
        showThirdMenu.value = false;
        activeSecondMenuIndex.value = null;
    }
};

// 处理二级菜单点击
const handleSecondMenuClick = (a: any, c: number, e: number) => {
    if (!a.children || a.children.length === 0) {
        // 没有三级菜单，直接执行原有逻辑
        selectmenuSecond(a, e);
    } else {
        // 有三级菜单，切换显示/隐藏
        if (activeSecondMenuIndex.value === a.key && showThirdMenu.value) {
            // 如果点击的是已展开的二级菜单，则隐藏三级菜单
            showThirdMenu.value = false;
            activeSecondMenuIndex.value = null;
        } else {
            // 显示此二级菜单的三级菜单
            activeSecondMenuIndex.value = a.key;
            showThirdMenu.value = true;
        }
    }
};

const showFirst = () => {
    // 保留函数但不做任何操作，因为我们已经改为点击触发
};

const hideFirst = () => {
    // 保留函数但不做任何操作，因为我们已经改为点击触发
};

const keepSecondMenuVisible = () => {
    showSecondMenu.value = true;
};

const keepThirdMenuVisible = () => {
    showThirdMenu.value = true;
};

const showThird = (a) => {
    // 此函数不再通过悬停触发显示三级菜单
    currentHoverItem.value = a.key;
};

const hideThird = () => {
    // 此函数保留，但三级菜单的隐藏现在通过点击控制
};

const hideSecond = () => {
    showThirdIndex.value = null;
    showSpin.value = false;
};

const selectmenuFirst = (i, e) => {
    if (i.children && i.children.length > 0) return; // 有子菜单的情况已在handleFirstMenuClick中处理
    selectShowFirstIndex.value = e;
    selectShowSecondIndex.value = null;
    selectShowThirdIndex.value = null;
    emits('update:value', i.key, i, null);
};

const selectmenuSecond = (a, c) => {
    if (a.children && a.children.length > 0) return; // 有三级菜单的情况已在handleSecondMenuClick中处理
    selectShowFirstIndex.value = c;
    selectShowSecondIndex.value = a.key;
    selectShowThirdIndex.value = null;
    emits('update:value', a.key, a);
};

const selectmenuThird = (a, b, e) => {
    selectShowFirstIndex.value = e;
    selectShowSecondIndex.value = a.key;
    selectShowThirdIndex.value = b.name;
    emits('update:value', b.name, a);
    
    // 选择三级菜单项后关闭三级菜单
    showThirdMenu.value = false;
    activeSecondMenuIndex.value = null;
};
</script>

<style scoped>
.top-menu-item-selected {
    position: relative;
    color: #000 !important;
    background-color: #ffffff;
    border-radius: 5px;
}

/* .top-menu-item-selected::before {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1px;
    transform: scale(1, 0.8);
    background-color: #000;
} */

.top-menu-item-selected2 {
    color: #000;
    /* position: relative; */
}

.top-menu {
    width: 100%;
    display: flex;
    align-items: center;
    height: 50px;
    position: relative;
}

.dropdown-arrow {
    color: #b0b1b1;
}

.top-menu-item-selected .dropdown-arrow {
    color: #000;
}

.top-menu-first:hover .dropdown-arrow {
    color: #000;
}

.top-menu-ellipsis {
    /* display: flex;
    align-items: center;
    justify-content: center; */
    /* width: 140px; */
    /* padding-left: 10px; */
    /* width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px; */
}

.top-menu-first {
    cursor: pointer;
    color: #b0b1b1;
    line-height: 1.75;
    position: relative;
    width: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
}

.top-menu-first:hover {
    color: #000;
}

.top-menu-ellipsis:hover {
    color: #000;
}

.top-menu-ellipsis:hover .top-menu-ellipsis-icon {
    --n-opacity: 1 !important;
}

.top-menu-ellipsis-icon-selected svg {
    fill: #000 !important;
}

.menu-second {
    display: flex;
    position: absolute;
    top: 50px;
    left: 0;
    width: 100%;
    background-color: #f8f9fb;
    z-index: 100;
    border-radius: 10px;
    padding: 0 0 10px 0;
    padding-left: 20px;
}

.top-menu-second {
    line-height: 1.75;
    border-radius: 3px;
    background: #f8f9fb;
    width: 140px;
    display: flex;
    flex-direction: column;
}

.top-menu-second-card {
    text-align: left;
    width: 140px;
    line-height: 40px;
    cursor: pointer;
    color: #b0b1b1;
    position: relative;
    display: block;
}

.top-menu-second-card > div {
    text-align: left;
    padding-left: 10px;
}

.secondText:hover {
    color: #000;
    /* position: relative; */
    display: inline-block;
    text-align: left;
}

/* 修改三级菜单样式，从右侧显示改为下方显示 */
.thirdMenu {
    position: relative;
    text-align: left;
    /* background-color: #fff; */
    z-index: 998;
    /* border-radius: 10px; */
    padding: 5px;
    width: 100%;
    /* margin-top: -5px; */
    /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); */
}

.thirdMenu > div {
    text-align: left;
    /* padding: 5px 0; */
    padding-left: 3px;
}

.thirdMenu > div:hover {
    color: #000;
}

/* 当三级菜单打开时，为二级菜单项添加一些空间 */
/* .top-menu-second-card.with-third-menu-open { */
    /* margin-bottom: 6px; 为三级菜单腾出一些空间 */
/* } */

/* 自定义tooltip样式 */
:deep(.n-ellipsis__tooltip) {
    background-color: rgba(0, 0, 0, 0.7) !important;
    color: white !important;
    border-radius: 4px !important;
    padding: 6px 10px !important;
}
</style>
